import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { approvalCommentApi } from '@/constant/apiURL/approval-comment';
import {
  normalizeLang,
  pickI18nText,
  type Lang,
} from '@/utils/approval/i18nText';
import { getNestedMetaDataFetcher } from '@/utils/apis';
import type {
  ApprovalCommentApiItem,
  ApprovalCommentItem,
  ApprovalCommentRow,
  ApprovalCommentThread,
  ApprovalLogItem,
  CommentSortOrder,
} from '@/components/Approval/ApprovalView/CommentSlot.types';
import { COMMENT_PAGE_SIZE } from '@/components/Approval/ApprovalView/CommentSlot.types';

/**
 * 품의 코멘트 데이터 layer — `GET /api/v2/approval/{documentId}/comments` (문서번호 기준).
 *
 * 스레드 전체가 한 응답으로 오므로 정렬·더보기는 클라이언트 처리이고, 코멘트 그룹 lifecycle
 * (그룹 조회/생성)과 `commentGroupId` 보관은 없다 — 재상신 시 서버가 스레드를 교체하므로
 * 이전 회차 그룹 ID 를 들고 있으면 등록이 깨진다.
 */

type ParsedThread = {
  /** 서버가 준 사용자 댓글 수(결재 로그 제외). 부재 시 null → 행에서 직접 센다. */
  commentCount: number | null;
  items: ApprovalCommentApiItem[];
};

const EMPTY_THREAD: ParsedThread = { commentCount: null, items: [] };

/**
 * envelope(`{data: {commentCount, logCount, comments}}`) 에서 스레드 배열·카운트를 뽑는다.
 * GET 응답과 작성 201 응답이 같은 형태라 두 경로가 이 파서를 공유.
 * 응답에 `commentGroupId` 도 실려 오지만 재상신 때 값이 바뀌므로 의도적으로 버린다.
 */
const parseThread = (envelope: unknown): ParsedThread => {
  const root =
    envelope && typeof envelope === 'object' && 'data' in (envelope as object)
      ? (envelope as { data: unknown }).data
      : envelope;

  if (!root || typeof root !== 'object') return EMPTY_THREAD;

  const body = root as ApprovalCommentThread;
  return {
    commentCount:
      typeof body.commentCount === 'number' ? body.commentCount : null,
    items: Array.isArray(body.comments) ? body.comments : [],
  };
};

/**
 * v2 는 ISO 8601(`2026-08-20T09:38:41+09:00`)로 내려주는데 표시 지점들이 문자열을 그대로
 * 렌더하므로 기존 표기로 평탄화. v1 표기(`YYYY-MM-DD HH:mm:ss`)도 같은 결과.
 */
const DISPLAY_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const toDisplayDate = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : raw;
};

const toTime = (raw: string | null | undefined): number => {
  const parsed = dayjs(raw ?? '');
  return parsed.isValid() ? parsed.valueOf() : 0;
};

/** 서버 정렬 파라미터가 없어진 대신 클라이언트 정렬. 동시각은 id 로 안정화. */
const sortItems = (
  items: ApprovalCommentApiItem[],
  order: CommentSortOrder,
): ApprovalCommentApiItem[] =>
  [...items].sort((a, b) => {
    const diff = toTime(a.createdAt) - toTime(b.createdAt);
    const byId = a.id.localeCompare(b.id);
    const signed = diff !== 0 ? diff : byId;
    return order === 'asc' ? signed : -signed;
  });

/**
 * 리스트 응답을 CommentSlot 이 사용하는 ApprovalCommentItem 형태로 정규화.
 *  - 다국어 필드(name/positionName 등)는 현재 로케일(`lang`)로 평탄화 (`pickI18nText`).
 */
const toCommentItem = (
  api: ApprovalCommentApiItem,
  currentUserPassportId: string,
  lang: Lang,
): ApprovalCommentItem => {
  const recommendList = (api.recommends ?? []).map((u) =>
    typeof u === 'string'
      ? { name: u, positionName: '' }
      : {
          name: pickI18nText(u.name, lang),
          positionName: pickI18nText(u.positionName, lang),
        },
  );
  return {
    type: 'comment',
    id: api.id,
    content: api.content,
    attachments: api.attachments ?? [],
    createdBy: {
      passportId: api.createdBy.passportId,
      name: pickI18nText(api.createdBy.name, lang),
      positionName: pickI18nText(api.createdBy.positionName, lang),
      departmentName: api.createdBy.departmentName,
      divisionName: api.createdBy.divisionName,
      nickName: api.createdBy.nickName,
      profileImageUrl: api.createdBy.profileImageUrl ?? null,
    },
    createdAt: toDisplayDate(api.createdAt),
    updatedAt: toDisplayDate(api.updatedAt || api.createdAt),
    isUpdated: !!api.updatedAt && api.updatedAt !== api.createdAt,
    // 삭제 코멘트는 v2 응답에서 제외 — 공용 CommentItem 이 요구하는 필드라 false 로 고정.
    isDeleted: false,
    // 본인 비교 — portal CommentItem 관습: passportId 기준.
    isDeleteEnable:
      !!currentUserPassportId &&
      api.createdBy.passportId === currentUserPassportId,
    // 공용 CommentItem 이 요구하는 feedback 묶음 — v2 는 agree/disagree 를 안 준다.
    feedback: {
      agree: [],
      agreeCount: 0,
      disagree: [],
      disagreeCount: 0,
      recommend: recommendList,
      recommendCount: api.recommendCount ?? 0,
      isAgree: false,
      isDisagree: false,
      isRecommend: api.isRecommend ?? false,
    },
  };
};

const toLogItem = (
  api: ApprovalCommentApiItem,
  lang: Lang,
): ApprovalLogItem => ({
  type: 'log',
  id: api.id,
  action: api.action ?? 'create',
  content: api.content || null,
  createdAt: toDisplayDate(api.createdAt),
  createdBy: {
    passportId: api.createdBy.passportId,
    name: pickI18nText(api.createdBy.name, lang),
    positionName: pickI18nText(api.createdBy.positionName, lang),
    departmentName: api.createdBy.departmentName,
    divisionName: api.createdBy.divisionName,
    nickName: api.createdBy.nickName,
    profileImageUrl: api.createdBy.profileImageUrl ?? null,
  },
});

const toRow = (
  api: ApprovalCommentApiItem,
  currentUserPassportId: string,
  lang: Lang,
): ApprovalCommentRow =>
  api.type === 'log'
    ? toLogItem(api, lang)
    : toCommentItem(api, currentUserPassportId, lang);

export type UseApprovalCommentsResult = {
  /** 현재 정렬에서 노출 중인 행 (더보기 슬라이스 적용) */
  rows: ApprovalCommentRow[];
  /** 표시용 댓글 수 — 서버 `commentCount`(결재 로그 제외) */
  commentCount: number;
  /** 정렬 토글 (localStorage 보존은 호출처가 책임) */
  orderType: CommentSortOrder;
  setOrderType: (next: CommentSortOrder) => void;
  /** 더 노출할 행이 남았는지 — 스레드 전체가 이미 메모리에 있으므로 길이 비교 */
  hasMore: boolean;
  /** "코멘트 더보기" — 노출 슬라이스 한 칸 확장 (네트워크 호출 없음) */
  loadMore: () => void;
  isLoading: boolean;
  error: unknown;
  /** 수정/삭제/공감 성공 후 호출 — 스레드 재조회 */
  refresh: () => Promise<void>;
  /** 작성 201 응답(스레드 전체)으로 캐시 교체 — 추가 조회 없음 */
  applyThread: (envelope: unknown) => Promise<void>;
};

const EMPTY_ROWS: ApprovalCommentRow[] = [];

/**
 * @param documentId 품의 단건 ID (문서번호, path param).
 * @param currentUserPassportId 본인 비교용. 없으면 isDeleteEnable 항상 false.
 * @param defaultOrder 정렬 초기값 (localStorage 복원은 CommentSlot 책임).
 */
const useApprovalComments = (
  documentId: string | null | undefined,
  currentUserPassportId: string,
  defaultOrder: CommentSortOrder = 'desc',
): UseApprovalCommentsResult => {
  // 다국어 평탄화용 현재 로케일 — useRelatedApprovals 와 동일 패턴.
  const { i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  const [orderType, setOrderTypeState] =
    useState<CommentSortOrder>(defaultOrder);
  // 노출 슬라이스 배수 — 1 = 첫 COMMENT_PAGE_SIZE 개.
  const [visibleChunks, setVisibleChunks] = useState(1);

  // defaultOrder 변경 동기화 (예: localStorage 복원 → mount 후)
  useEffect(() => {
    setOrderTypeState(defaultOrder);
  }, [defaultOrder]);

  useEffect(() => {
    setVisibleChunks(1);
  }, [documentId]);

  const setOrderType = useCallback((next: CommentSortOrder) => {
    setOrderTypeState(next);
    setVisibleChunks(1);
  }, []);

  const {
    data,
    error,
    isLoading,
    mutate: mutateThread,
  } = useSWR<ParsedThread>(
    documentId ? (['approval-comments', documentId] as const) : null,
    async ([, id]: readonly [string, string]) =>
      parseThread(
        await getNestedMetaDataFetcher(approvalCommentApi.COMMENTS(id)),
      ),
    {
      // 다른 사용자가 재상신하면 서버 스레드가 교체되는데 열어둔 화면엔 알림 경로가 없다.
      // 폴링 대신 포커스 복귀로만 재검증 (스로틀 30초).
      revalidateOnFocus: true,
      focusThrottleInterval: 30_000,
    },
  );

  /**
   * 반려요청(return_request) 로그는 걷어낸다.
   *
   * 반려요청은 결재 액션이 아니라 기안자가 현재 결재자에게 "반려해 달라"고 부탁하는 것이고,
   * 실제 반려는 요청받은 결재자가 한다. 결재 이력에 승인·반려와 나란히 쌓이면
   * 결재가 이루어진 것처럼 읽히고, 문서를 보는 모든 사람에게 사유가 노출된다.
   * 사유는 조회 화면 상단 안내(ReturnRequestNotice)가 요청받은 결재자와 기안자에게만 보여준다.
   *
   * 서버는 2026-09-09 부터 이 로그를 남기지 않는다(PORTAL-14138). 그전에 적립된 행이 스레드에
   * 그대로 남아 있어 필터는 유지한다 — 지우면 과거 요청 사유가 열람자 전원에게 다시 노출된다.
   */
  const sortedItems = useMemo(
    () =>
      sortItems(
        (data?.items ?? []).filter((item) => item.action !== 'return_request'),
        orderType,
      ),
    [data, orderType],
  );

  const visibleCount = visibleChunks * COMMENT_PAGE_SIZE;

  const rows = useMemo<ApprovalCommentRow[]>(() => {
    if (sortedItems.length === 0) return EMPTY_ROWS;
    return sortedItems
      .slice(0, visibleCount)
      .map((api) => toRow(api, currentUserPassportId, lang));
  }, [sortedItems, visibleCount, currentUserPassportId, lang]);

  const commentCount = useMemo(() => {
    if (typeof data?.commentCount === 'number') return data.commentCount;
    // 응답에 commentCount 가 없을 때만 — 결재 로그를 제외한 사용자 댓글만 센다.
    return (data?.items ?? []).filter((item) => item.type !== 'log').length;
  }, [data]);

  const hasMore = sortedItems.length > visibleCount;

  const loadMore = useCallback(() => {
    setVisibleChunks((prev) => prev + 1);
  }, []);

  const refresh = useCallback(async () => {
    await mutateThread();
  }, [mutateThread]);

  const applyThread = useCallback(
    async (envelope: unknown) => {
      await mutateThread(parseThread(envelope), { revalidate: false });
    },
    [mutateThread],
  );

  return {
    rows,
    commentCount,
    orderType,
    setOrderType,
    hasMore,
    loadMore,
    isLoading,
    error,
    refresh,
    applyThread,
  };
};

export default useApprovalComments;
